import { observer } from 'mobx-react-lite'
import { withPrefix } from 'gatsby'
import React, { useRef, useState } from 'react'
import { useStore } from '../../../hooks/useStore'
import { RICH_TEXT_OPTIONS } from '../../../utils/rich-text-config'
import { PillButton } from '../buttons/pill-button'
import { SmallHeadline } from '../headlines'
import RichText from '../rich-text/RichText'
import { SmartLinkExternal } from '../link'

const EmailCaptureSection = React.memo(({data}) => {
    return (
        <div key="em-c-s" className='email-capture-section flex--center'>
            <div className='elevate'>
                <div className="text-section">
                    <RichText>{data.homePageEmailSignupCopy}</RichText>
                    <RichText>{data.homePageEmailSignupBody}</RichText>
                </div>

                <EmailInputSection/>
                <EmailDisclaimer/>
            </div>
            <EmailSectionBackgroundImages/>
            <style jsx>{`
                .email-capture-section {
                    background: var(--color--green);
                    height: 500px;
                    position: relative;
                }

                .email-capture-section :global(.headline--l), .email-capture-section .text-section :global(p) {
                    color: white;
                }
            
                .email-capture-section .text-section :global(p) {
                    margin: 20px 0;
                }

                .text-section {
                    margin-bottom: 30px;
                    text-align: center;
                }

                @media (pointer: coarse), (max-width: 769px) {
                    .email-capture-section {
                        height: 700px;
                    }

                    .text-section {
                        padding: 20px;
                    }
                }
                
            `}</style>
        </div>
    )
})

const EmailInputSection = observer(() => {
    const {uiStore:{submitEmail, home__emailSubmited, home__emailError}} = useStore();
    const [isFocused, setFocus] = useState(false);
    const emailRef = useRef(null);

    return (
        <div className={home__emailError ? 'email-input-section flex--center error' : 'email-input-section flex--center'}>
            <div className="flex">
                <input 
                    ref={emailRef}
                    className={isFocused ? "focused" : ""}
                    onFocus={() => setFocus(true)}
                    onBlur={() => setFocus(false)}
                    type="text" 
                    placeholder='your@email.com'/>
                <PillButton onClick={() => submitEmail(emailRef)}>{home__emailSubmited ? "Thank you!" : "Subscribe Now"}</PillButton>
            </div>
            

            <style jsx>{`

                
                input {
                    background: rgba(0,0,0,.4);
                    color: white;
                    border-radius: 8px;
                    margin-right: 20px;
                    text-align: center;
                    padding: 0 20px;
                    transition: background .24s ease;
                    width: 280px
                }

                .focused::placeholder { 
                    opacity: 0;
                }

                input::placeholder { /* Chrome, Firefox, Opera, Safari 10.1+ */
                    opacity: .5;
                }

                input:hover {
                    background: rgba(0,0,0,.5);
                }
                


                input.focused {
                    background: rgba(0,0,0,.5);
                }

                
                
                ::placeholder { /* Chrome, Firefox, Opera, Safari 10.1+ */
                    color: white;
                    opacity: 1; /* Firefox */
                }

                .error input, .error input::placeholder{
                    color: red;
                }

                .error input {
                    animation: shake 0.82s cubic-bezier(.36,.07,.19,.97) both;
                }
                
                @keyframes shake {
                    10%, 90% {
                      transform: translate3d(-1px, 0, 0);
                    }
                    
                    20%, 80% {
                      transform: translate3d(2px, 0, 0);
                    }
                  
                    30%, 50%, 70% {
                      transform: translate3d(-4px, 0, 0);
                    }
                  
                    40%, 60% {
                      transform: translate3d(4px, 0, 0);
                    }
                }


                  @media (pointer: coarse), (max-width: 769px) {
                        .email-input-section {
                            padding: 0 14px;
                        }
                      .email-input-section > div{
                          display: block;
                      }

                      input {
                          width: 100%;
                          height: 38px;
                          margin-bottom: 12px;
                      }

                      .email-input-section :global(.pill-button){
                        width: 100%;
                    }
                  }
            `}</style>
        </div>
    )
})

const EmailSectionBackgroundImages = () => {
    return (
        <>
            <img className='lmbg-tree' src={withPrefix('/images/home-tree-2x.webp')} alt="" />
            <img className='lmbg-rocks' src={withPrefix('/images/home-rocks-2x.webp')} alt="" />
            <img className='lmbg-bird' src={withPrefix('/images/home-bird-2x.webp')} alt="" />

            <style jsx>{`
                img {
                    position: absolute;
                }

                .lmbg-tree {
                    height: 340px;
                    left: 60px;            
                }

                .lmbg-rocks {
                    right: 300px;
                    bottom: 30px;
                    height: 90px;
                }

                .lmbg-bird {
                    height: 220px;
                    right: 80px;
                    top: 20px;
                }

                @media (max-width: 769px), (pointer: coarse) {
                    .lmbg-tree {
                        top: 40px;
                        height: 180px;
                        left: 30%;
                    }

                    .lmbg-rocks {
                        right: initial;
                        left: 5%;
                        bottom: 50px;
                        height: 120px;
                    }
    
                    .lmbg-bird {
                        height: 150px;
                        right: 0%;
                        top: initial;
                        bottom: 0px;
                    }
                }
            `}</style>
        </>
    )
}

const EmailDisclaimer = () => {
    return (
        <div className='flex--center email-disclaimer'>
            <div className='elevate text'>
                <h6 className='text-xs'>You can unsubscribe at any time using the link in our emails.</h6>
                <h6 className='text-xs'>For more details, review our <SmartLinkExternal to="https://www.intuit.com/privacy/statement/">privacy policy.</SmartLinkExternal></h6>

            </div>

            <style jsx>{`

                .text {
                    text-align: center;
                    color: white;
                    margin-top: 100px;
                }

                .email-disclaimer :global(a) {
                    text-decoration: underline;
                }
                h6 {
                    line-height: 1.5;
                    font-size: 10px;

                }

                @media (max-width: 769px), (pointer: coarse) {
                    .text {
                        margin-top: 60px;
                    }
                }
            `}</style>
        </div>
    )
}


export default EmailCaptureSection